import { useEffect } from "react";
import { formatMoney, formatDate, formatCount, carryLine } from "./billingFormat";

/**
 * The last screen before Razorpay opens. Every purchase says the same four
 * things in the same order: what you get, until when, what it costs, and what
 * changes or ends.
 *
 *   kind "topup"   -> packs added to the current plan, usable until it ends
 *   kind "upgrade" -> new plan from today, unused credits come along, old plan ends
 *   kind "new"     -> new plan from today (nothing to carry)
 */
export default function PurchaseConfirmModal({
  open, onClose, onConfirm, busy,
  kind, plan, packs = 1, options,
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose, busy]);

  if (!open || !options) return null;

  const current = options.current || {};
  const carry = options.carry || {};
  let title = "";
  let amount = "";
  let lines = [];
  let totalLabel = "";
  let totalValue = "";

  if (kind === "topup") {
    const t = options.topup || {};
    const interviews = t.interviews_per_pack * packs;
    const cvs = t.cvs_per_pack * packs;
    amount = formatMoney(t.pack_price * packs, t.currency);
    title = `Add ${formatCount(interviews)} interviews · ${amount}`;
    lines = [
      <>
        <b>{formatCount(interviews)} interviews</b> and <b>{formatCount(cvs)} CV screenings</b> are
        added to your {current.plan_name} plan <b>right away</b>.
      </>,
      <>
        Use them until your plan ends on <b>{formatDate(t.valid_until)}</b>. Any you don't use
        expire then.
      </>,
      <>Your plan and its end date don't change.</>,
    ];
    const left = options.projections || {};
    totalLabel = `You'll have until ${formatDate(t.valid_until, { year: false })}`;
    totalValue = `${formatCount((left.interviews?.left || 0) + interviews)} interviews · ${formatCount((left.cvs?.left || 0) + cvs)} CVs`;
  } else if (plan) {
    const entry = (options.plans || {})[plan.plan_id] || {};
    const cf = plan.countable_features || {};
    const planInterviews = Number(cf.interview_count_v2) || 0;
    const planCvs = Number(cf.resume_screening_count) || 0;
    const users = Number(cf.hr_count) || 0;
    const carrying = kind === "upgrade";
    const moved = carrying ? carryLine(carry) : "";
    amount = formatMoney(plan.amount, plan.currency);
    title = `${carrying ? "Move to" : "Buy"} ${plan.display_name} · ${amount}`;
    lines = [
      <>
        {plan.display_name} starts today and runs until <b>{formatDate(entry.ends_on)}</b>.
      </>,
      <>
        You get <b>{formatCount(planInterviews)} interviews</b>, <b>{formatCount(planCvs)} CV screenings</b>
        {users ? <> and up to <b>{users} users</b></> : null}.
      </>,
    ];
    if (moved) lines.push(<b>{moved}</b>);
    if (carrying && current.plan_name) {
      lines.push(
        current.is_trial
          ? <>Your Free plan ends today.</>
          : <>Your {current.plan_name} plan ends today. Its remaining days aren't refunded.</>
      );
    }
    totalLabel = `You'll have until ${formatDate(entry.ends_on, { year: false })}`;
    totalValue = `${formatCount(planInterviews + (carrying ? carry.interviews || 0 : 0))} interviews · ${formatCount(planCvs + (carrying ? carry.cvs || 0 : 0))} CVs`;
  }

  return (
    <div
      className="fixed inset-0 z-[1450] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white dark:bg-[#20202B] rounded-2xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-[17px] font-semibold text-gray-900 dark:text-white">{title}</h2>
        <ul className="mt-4 space-y-2.5 text-[13px] text-gray-600 dark:text-gray-300 list-disc pl-5">
          {lines.map((line, i) => <li key={i}>{line}</li>)}
        </ul>
        <div className="mt-4 pt-3 border-t border-[#EBEAED] dark:border-gray-700/60 flex items-center justify-between gap-3 text-[13px]">
          <span className="text-gray-500 dark:text-gray-400">{totalLabel}</span>
          <span className="font-semibold text-gray-900 dark:text-white">{totalValue}</span>
        </div>
        <div className="mt-5 flex gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-2.5 rounded-full text-[13px] font-semibold border border-[#EBEAED] dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 py-2.5 rounded-full text-[13px] font-semibold text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 disabled:opacity-60"
          >
            {busy ? "Opening checkout..." : `Pay ${amount}`}
          </button>
        </div>
      </div>
    </div>
  );
}
